import { useParams } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Badge,
  ButtonLink,
  Card,
  DataRow,
  ErrorState,
  ExternalButtonLink,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { useSession } from '@/features/auth/sessionContext';
import { DownloadInvoiceButton } from '@/features/orders/DownloadInvoiceButton';
import { PayAgainButton } from '@/features/orders/PayAgainButton';
import { PriceBreakdown } from '@/features/pricing/PriceBreakdown';
import { paths } from '@/routes/paths';
import { useOrderByNumber } from '@/lib/api/checkout';
import { useMeta } from '@/lib/api/meta';
import { formatClockTime, formatDateTime, formatDayRange, formatPaise } from '@/lib/format';
import type { OrderDetail } from '@/lib/api/types';

/**
 * /orders/:orderNo — where the payment gateway sends the customer back to.
 *
 * This page **reads**. It never tells the server anything about the payment, and
 * it ignores the `?status=` the gateway appends, because a browser that came
 * back proves nothing: the customer could have closed the tab after paying, or
 * pressed back without paying at all. The order is whatever the verified webhook
 * made it.
 *
 * That creates a visible race — the browser often wins by a second or two — so
 * while the order is still `awaiting_payment` the query polls and the page says
 * plainly that we are waiting to hear from the bank, rather than showing a
 * "payment failed" that is really "payment not confirmed yet".
 */
export function OrderTrackingPage() {
  const { orderNo } = useParams<{ orderNo: string }>();
  const session = useSession();
  // Wait for the refresh cookie to be exchanged before asking for the order:
  // arriving here straight from the gateway, the in-memory access token is gone.
  const sessionSettled = session.status !== 'loading';
  const {
    data: order,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useOrderByNumber(orderNo, { enabled: session.status === 'authenticated' });

  if (!sessionSettled || (session.status === 'authenticated' && isLoading)) {
    return (
      <AppShell title="Your order">
        <SkeletonGroup label="Loading your order">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </SkeletonGroup>
      </AppShell>
    );
  }

  if (session.status === 'anonymous') {
    return (
      <AppShell title="Your order">
        <Alert severity="info" title="Sign in to see this order">
          <p>
            Your order {orderNo} is safe. Sign in with the mobile number you used and it will be
            here.
          </p>
          <p className="mt-3">
            <ButtonLink to={paths.signIn} state={{ from: `/orders/${orderNo ?? ''}` }}>
              Sign in
            </ButtonLink>
          </p>
        </Alert>
      </AppShell>
    );
  }

  if (isError || !order) {
    return (
      <AppShell title="Your order">
        <ErrorState
          error={error}
          title="We could not find that order"
          onRetry={() => void refetch()}
          retrying={isFetching}
        />
      </AppShell>
    );
  }

  return (
    // `wide`: this page lays out its own two columns, so it needs the shell's
    // full width rather than the reading cap that protects a form.
    <AppShell title={`Order ${order.order_no}`} subtitle={order.status_label} wide>
      {/* Two columns on desktop: what was ordered on the left, what has happened
          to it on the right. Stacked they were a scroll apart, which is the one
          comparison this page exists to make. The explicit gap also fixes cards
          that sat flush against each other. */}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
        <div className="lg:col-span-2">
          <StatusBanner order={order} />
        </div>

        <div className="flex flex-col gap-4">
          <Card title="Order">
            <DataRow
              label="Status"
              value={<Badge tone={badgeTone(order.status)}>{order.status_label}</Badge>}
            />
            <DataRow label="Placed" value={formatDateTime(order.created_at)} />
            {/* One row per document: with several files there is no single page
            count or copy count to show. */}
            {order.documents.map((document, index) => (
              <DataRow
                key={document.position}
                label={
                  order.documents.length > 1
                    ? (document.filename ?? `Document ${index + 1}`)
                    : 'Pages'
                }
                value={`${document.page_count} × ${document.copies} ${
                  document.copies === 1 ? 'copy' : 'copies'
                }`}
              />
            ))}
            <DataRow label="Sheets" value={String(order.sheets)} />
            <DataRow
              label={order.fulfilment_method === 'pickup' ? 'Collection' : 'Delivery'}
              value={order.fulfilment_method === 'pickup' ? 'From the shop' : 'To your address'}
            />
            {/* Present from the moment it is paid for, so the screen the
                customer lands on after paying answers "when?" rather than
                making them wait for a courier to be booked. Once one is, this
                becomes the courier's own dated estimate. */}
            {order.delivery_estimate ? (
              <DataRow
                label="Expected delivery"
                value={formatDayRange(
                  order.delivery_estimate.from_date,
                  order.delivery_estimate.to_date,
                )}
              />
            ) : null}
            <DataRow label="Total" value={formatPaise(order.total_paise)} />
            {order.refunded_paise > 0 ? (
              <DataRow label="Refunded" value={formatPaise(order.refunded_paise)} />
            ) : null}
          </Card>

          {order.tracking ? (
            <Card title="Delivery">
              {/* Only when we know who is actually carrying it. The field is
                  empty until the courier is assigned, and an empty row reads
                  as a missing value rather than as "not decided yet". */}
              {order.tracking.courier_name ? (
                <DataRow label="Courier" value={order.tracking.courier_name} />
              ) : null}
              {/* A hand-booked parcel's "AWB" is our own reference unless the
                  service gave one; the made-up kind starts with MANUAL- and is
                  nothing the customer can do anything with. */}
              {order.tracking.provider === 'manual' ? (
                order.tracking.awb.startsWith('MANUAL-') ? null : (
                  <DataRow label="Reference" value={order.tracking.awb} />
                )
              ) : (
                <DataRow label="Tracking number" value={order.tracking.awb} />
              )}
              {/* No link out to the aggregator's tracking site: whose label we
                  buy is not something the customer needs to know, and the URL
                  says it plainly. The carrier and the AWB are here, which is
                  what tracking actually needs, and operators get the link on
                  the admin order screen. */}
            </Card>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          <Card title="Progress">
            {order.timeline.length === 0 ? (
              <p className="text-sm text-muted">Nothing has happened yet.</p>
            ) : (
              <ol className="flex flex-col gap-3">
                {order.timeline.map((entry) => (
                  <li key={`${entry.status}-${entry.occurred_at}`} className="flex flex-col">
                    <span className="font-medium">{entry.label}</span>
                    <span className="text-xs text-muted">{formatDateTime(entry.occurred_at)}</span>
                    {entry.note ? <span className="text-sm text-muted">{entry.note}</span> : null}
                  </li>
                ))}
              </ol>
            )}
          </Card>

          {order.breakdown ? (
            <PriceBreakdown
              breakdown={order.breakdown}
              totals={{
                sheets: order.sheets,
                weight_g: 0,
                print_cost_paise: order.subtotal_paise - order.shipping_paise,
                // Not read by PriceBreakdown, which shows the itemised binding row
                // straight from `breakdown`. Present because the shape requires it.
                binding_cost_paise: 0,
                subtotal_paise: order.subtotal_paise,
                shipping_paise: order.shipping_paise,
                shipping_waived_paise: 0,
                discount_paise: order.discount_paise,
                tax_paise: order.tax_paise,
                total_paise: order.total_paise,
                total_display: order.total_display,
              }}
            />
          ) : null}

          {order.paid_at ? (
            <div>
              <DownloadInvoiceButton orderId={order.id} orderNo={order.order_no} />
            </div>
          ) : null}
        </div>
      </div>
    </AppShell>
  );
}

/** The one line the customer actually came back to read. */
function StatusBanner({ order }: { order: OrderDetail }) {
  if (order.status === 'awaiting_payment') {
    return (
      <Alert severity="info" title="Waiting for your payment to be confirmed">
        <p>
          We have not heard from the payment provider yet. This page updates on its own — it usually
          takes a few seconds.
        </p>
        {/* The original link first — it is the session already open on the
            customer's phone, and reusing it avoids a second attempt at the
            provider. But it lives an hour, so it cannot be the only route:
            anyone returning the next morning gets a dead page that looks
            like a broken shop. Pay again opens a fresh one. */}
        {order.payment?.checkout_url ? (
          <p className="mt-2">
            <ExternalButtonLink href={order.payment.checkout_url} variant="secondary">
              Go back to payment
            </ExternalButtonLink>
          </p>
        ) : null}
        <PayAgainButton order={order} />
      </Alert>
    );
  }

  if (order.status === 'payment_failed') {
    return (
      <Alert severity="danger" title="That payment did not go through">
        <p>{order.payment?.failure_reason ?? 'No money has been taken. You can try again.'}</p>
        {/* The whole reason this component exists. The quote was consumed
            when the order was placed, so the cart is empty and checkout
            cannot be re-run — this is the only route back to paying. */}
        <PayAgainButton order={order} />
      </Alert>
    );
  }

  if (order.status === 'cancelled') {
    return (
      <Alert severity="info" title="This order was cancelled">
        <p>{order.cancel_reason ?? 'Nothing will be printed.'}</p>
      </Alert>
    );
  }

  if (order.status === 'refunded') {
    return (
      <Alert severity="info" title="This order was refunded">
        <p>
          {formatPaise(order.refunded_paise)} has been returned to your original payment method.
        </p>
      </Alert>
    );
  }

  const pickup = order.fulfilment_method === 'pickup';

  if (order.status === 'delivered') {
    // Nobody delivered anything to a customer who walked to the counter.
    return <Alert severity="success" title={pickup ? 'Collected' : 'Delivered'} />;
  }

  // The one moment a collection customer is waiting for. The details screen
  // promised "you will see it move to Ready to collect"; keeping the
  // "Payment received" banner here contradicted the badge beside it.
  if (order.status === 'ready_for_pickup') {
    return <ReadyToCollect orderNo={order.order_no} />;
  }

  if (order.status === 'in_production' || order.status === 'printed') {
    return (
      <Alert severity="info" title="Printing">
        <p>
          {pickup
            ? 'We will tell you here when it is ready to collect.'
            : 'It goes out with a rider as soon as it is packed.'}
        </p>
      </Alert>
    );
  }

  if (
    order.status === 'packed' ||
    order.status === 'shipped' ||
    order.status === 'out_for_delivery'
  ) {
    return (
      <Alert severity="info" title="On its way">
        <p>
          {order.status === 'packed'
            ? 'Packed and waiting for the rider.'
            : order.tracking?.courier_name
              ? `With ${order.tracking.courier_name}.`
              : 'The rider has it.'}
        </p>
      </Alert>
    );
  }

  return (
    <Alert severity="success" title="Payment received">
      <p>Your order is confirmed and the shop has it. We will keep this page up to date.</p>
    </Alert>
  );
}

/**
 * Where to go and what to say. The address and hours come from the server's
 * bootstrap metadata — the same identity printed on the invoice — so a move
 * or a change of hours is one edit, not a hunt through the client.
 */
function ReadyToCollect({ orderNo }: { orderNo: string }) {
  const { data: meta } = useMeta();
  const shop = meta?.shop;
  const opens = formatClockTime(meta?.printing_opens_at);
  const closes = formatClockTime(meta?.printing_closes_at);

  return (
    <Alert severity="success" title="Ready to collect">
      {shop ? <p>{[shop.name, ...shop.address_lines].join(', ')}</p> : null}
      {opens && closes ? (
        <p>
          Open {opens} to {closes}
          {shop?.phone ? ` · ${shop.phone}` : ''}
        </p>
      ) : null}
      <p>Quote order number {orderNo} at the counter.</p>
    </Alert>
  );
}

function badgeTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'delivered' || status === 'paid') return 'success';
  if (status === 'payment_failed' || status === 'cancelled') return 'danger';
  if (status === 'awaiting_payment') return 'warning';
  return 'neutral';
}
